import { RecentMessagesProps } from "@/common/types";
import { generateDateTimeInfo } from "@/utils/date.util";
import NewMessage from "./new-message";
import { useCallback, useEffect, useRef, useState } from "react";
import { RootState } from "@/redux/reducers";
import { useSelector } from "react-redux";

const RecentMessages: React.FC<RecentMessagesProps> = ({
  recentMessages,
  handleRowClick,
  selectedChat,
  handleNewMessage,
  loadNextPage,
  nextPagePresent,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);

  const { unreadCount } = useSelector((state: RootState) => state.chat);

  const lastRowRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreRows();
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, hasMore]
  );

  const loadMoreRows = async () => {
    setLoading(true);
    loadNextPage();
    setLoading(false);
  };

  useEffect(() => {
    setHasMore(nextPagePresent);
  }, [nextPagePresent]);

  return (
    <>
      <div className="flex items-center justify-between mx-4 mt-2 pb-2 border-b">
        <p className="font-medium">
          Messages{" "}
          {(unreadCount as number) > 0 && (
            <span className="text-gray-500">{`(${
              unreadCount as number
            })`}</span>
          )}
        </p>
        <NewMessage handleNewMessage={handleNewMessage} />
      </div>
      {recentMessages.map((message, idx) => (
        <div
          key={idx}
          className={`grid grid-cols-6 py-3 mx-4 pr-4 border-b border-solid border-gray-200 hover:bg-secondary ${
            selectedChat === message.other_user.id ? "bg-gray-100" : ""
          }`}
          onClick={() => handleRowClick(message)}
          ref={idx === recentMessages.length - 1 ? lastRowRef : null}
        >
          <div className="col-span-6 flex flex-row items-center justify-start">
            <div
              className={`w-[10px] h-[10px] rounded-full mr-2 ${
                message.is_read ? "bg-transparent" : "bg-black"
              }`}
            ></div>
            <div className="flex flex-col justify-between w-[calc(100%-20px)]">
              <div className="text-sm font-medium mb-1">
                {message.other_user.name}
              </div>
              <div className="flex justify-between gap-2 w-full">
                <div className="text-sm text-gray-500 truncate">
                  {message.last_message?.send_by_me ? "You:" : ""}{" "}
                  {message.last_message?.message}
                </div>
                <div className="text-sm text-gray-500 w-fit flex-none">
                  {generateDateTimeInfo(message.last_message?.timestamp)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      {recentMessages.length === 0 && (
        <div className="flex items-center justify-center h-[calc(100vh-200px)] mx-auto">
          <p className="text-center text-sm w-[250px]">
            No chats yet. Start a new chat with your friends!
          </p>
        </div>
      )}
    </>
  );
};

export default RecentMessages;
